import moment from "moment-timezone";
import { logError } from "services/logging";
import type { UiLanguage } from ".";

// moment bundles English only; the rest load as their own chunk so no one pays for locales
// they will not use.
const LOCALE_LOADERS: Record<UiLanguage, (() => Promise<unknown>) | null> = {
  en: null,
  de: () => import("moment/locale/de"),
  es: () => import("moment/locale/es"),
  fr: () => import("moment/locale/fr"),
  pt: () => import("moment/locale/pt"),
  zh: () => import("moment/locale/zh-cn"),
};

// moment's own codes, which do not always match our UI language codes.
const LOCALE_CODES: Record<UiLanguage, string> = {
  en: "en",
  de: "de",
  es: "es",
  fr: "fr",
  pt: "pt",
  zh: "zh-cn",
};

/**
 * Points moment at `language`, loading its locale data first. The locale is global mutable state
 * that React does not track, so callers must await this before triggering the re-render that
 * shows the new language.
 */
export async function applyMomentLocale(language: UiLanguage): Promise<void> {
  const load = LOCALE_LOADERS[language];
  if (load) {
    try {
      await load();
    } catch (error) {
      logError(`Failed to load the ${language} date locale`, error);
      return;
    }
  }

  moment.locale(LOCALE_CODES[language]);
}
